import { Component, Input, OnInit } from '@angular/core';
import { ChartsModule, SeriesLabelsContentArgs } from '@progress/kendo-angular-charts';
import { DataService } from 'app/services/data.service';
import { NgFor, CommonModule } from '@angular/common';
import { IntlService } from '@progress/kendo-angular-intl';
import { Color } from '@progress/kendo-drawing';
import { Dictionary, List } from 'lodash';
import { environment } from 'environments/environment';

@Component({
  selector: 'app-drake-chart',
  standalone: true,
  templateUrl: './drake-chart.component.html',
  styleUrls: ['./drake-chart.component.scss'],
  imports: [ChartsModule, NgFor, CommonModule]
})
export class DrakeChartsComponent implements OnInit {


  axisByType: Record<string, any> = {};
  legendOpts = { position: 'bottom', orientation: 'horizontal' };
  chartAreaOpts = { background: 'transparent' };
  plotAreaOpts = { margin: { top: 8, right: 8, bottom: 8, left: 8 } };
  tooltipOpts = { visible: true, shared: true, format: '{0:n0}' };
  seriesDefaultsOpts = { gap: 1.2, spacing: 0.2, labels: { visible: false }, transitions: false }; 


  categories: string[] = [];
  title: string= '';
  series: { name: string, data: any[], type: string, color: string, configuration: {[key: string] : string} }[] = [];
  categoryColors: { [key: string]: string } = {};
  chartTypes: string[] = [];
  seriesByType: { [key: string]: any[] } = {}; 
  @Input() public chartsId: any = "";
  colorsArray: string[] = environment.chartColors;

  constructor(private dataService: DataService, private intl: IntlService) {
    this.labelContent = this.labelContent.bind(this);
  }

  getAxis(type: string) {
  return {
    categories: this.categories,
    line: { visible: false },
    majorGridLines: { color: '#eee' },
    labels: {
      rotation: type === 'column' ? 'auto' : 0, 
      format: '{0}'
    }
  };
}

private getFieldLabel(groupField: string | undefined, fallback: string): string {
  if (!groupField) return fallback;
  const parts = groupField.split('.');
  return parts.length > 1 ? parts[1] : groupField; 
}

getNextColor() {
  const i = this.series.length % this.colorsArray.length;
  return this.colorsArray[i];
}

  ngOnInit(): void {
    this.dataService.getChartsData(this.chartsId).subscribe(response => {
      const dataResponse = response.data;

      //const categoriesResponse = dataResponse[0].Categories;
     // const seriesResponse = dataResponse[0].Series;
     
      const isInvalid = (s: any) => s == null || `${s}`.trim() === '' || s === '#N/A#';

      const rawCategories = dataResponse[0].Categories || [];
      const rawSeries = dataResponse[0].Series || [];

      const validIdx: number[] = [];
      const categoriesResponse = rawCategories
        .map((c: any, i: number) => ({ name: c?.name, i }))
        .filter(({ name, i }) => {
          if (!isInvalid(name)) { validIdx.push(i); return true; }
          return false;
        })
        .map(({ name }) => ({ name })); 

      const seriesResponse = rawSeries
        .filter((s: any) => !isInvalid(s?.name))
        .map((s: any) => {
          const copy = { ...s };
          if (copy.type !== 'pie' && Array.isArray(copy.data)) {
            copy.data = validIdx.map((idx) => copy.data[idx]); 
          }
          return copy;
        });

      this.title = dataResponse[0].Title;
      var categoryColorResponse = dataResponse[0].CategoryColor;
      if (categoryColorResponse === "RANDOM" || categoryColorResponse === "SEQUENCE") {
        
        let availableColors = [...this.colorsArray];
        const categoryColorsMapping: { [key: string]: string } = {};

        categoriesResponse.forEach((category, i) => {
          if (!categoryColorsMapping.hasOwnProperty(category.name)) {
            if (availableColors.length === 0) {
              throw new Error("Non ci sono abbastanza colori per tutte le categorie iniziali.");
            }
            const index = categoryColorResponse === "RANDOM" ? Math.floor(Math.random() * availableColors.length) : i % availableColors.length;
            const chosenColor = availableColors[index];
            availableColors.splice(index, 1);
            categoryColorsMapping[category.name] = chosenColor;
          }
        });

        seriesResponse.forEach((seriesItem,i) => {
          if (!categoryColorsMapping.hasOwnProperty(seriesItem.name)) {
            if (availableColors.length === 0) {
              console.warn(`Non ci sono colori disponibili per la categoria ${seriesItem.name}`);
              categoryColorsMapping[seriesItem.name] = "#000000";
            } else {
              const index = categoryColorResponse === "RANDOM" ? Math.floor(Math.random() * availableColors.length) : i % availableColors.length;
              const chosenColor = availableColors[index];
              availableColors.splice(index, 1);
              categoryColorsMapping[seriesItem.name] = chosenColor;
            }
          }
        });

        categoryColorResponse = Object.keys(categoryColorsMapping).map(key => {
          return { Name: key, Color: categoryColorsMapping[key] };
        });
      }

      this.categoryColors = categoryColorResponse.reduce((acc: any, item: any) => {
        acc[item.Name] = item.Color;
        return acc;
      }, {});

      this.categories = categoriesResponse.map((cat: any) => cat.name);

      const pieDataMap: { [key: string]: number } = {};
      seriesResponse.forEach((serie: any) => {
        if (serie.type === 'pie') {
          this.categories.forEach((category, index) => {
            if (!pieDataMap[category]) {
              pieDataMap[category] = 0;
            }
            pieDataMap[category] += serie.data[index] || 0;
          });
        }
      });

      const pieData = Object.keys(pieDataMap)
        .filter(category => category)
        .map(category => ({
          category: category || 'N/D',
          value: pieDataMap[category],
          configuration: null,
          color: this.categoryColors[category] || 'grey'
        }));

      if (Object.keys(pieDataMap).length > 0) {
        this.series.push({
          name: 'Pie Data',
          data: pieData,
          type: 'pie',
          configuration: null,
          color: 'black'
        });
      }

      seriesResponse.forEach((serie: any, index: number) => {
        if (serie.type !== 'pie') {

          let configurationObj = {};

          if (typeof serie.configuration === 'string') {
            try {
              configurationObj = JSON.parse(serie.configuration);
            } catch (e) {
              console.error('Invalid configuration format:', e);
            }
          } else {
            configurationObj = serie.configuration;
          }

          this.series.push({
            name: serie.name || 'N/D',
            data: serie.data,
            type: serie.type,
            configuration : configurationObj,
            color: this.categoryColors[serie.name] || '#a4a2a8'
          });
        }

        
      });

      this.chartTypes = [...new Set(this.series.map(s => s.type))];

      // Pre-elaborazione delle serie per ogni tipo di grafico
      this.chartTypes.forEach(chartType => {
        this.axisByType[chartType] = {
          categories: this.categories,
          line: { visible: false },
          majorGridLines: { color: '#eee' },
          labels: { rotation: chartType === 'column' ? 'auto' : 0, format: '{0}' }
        };
        this.seriesByType[chartType] = this.series.filter(s => s.type === chartType);
      });

      console.log("series: ", this.series);
      console.log("chartTypes: ", this.chartTypes);
      console.log("categoryColors: ", this.categoryColors);

    });
  }

  labelContent(e: any): string {
    return `${e.category}: ${e.value}`;
  }
}
